import { Injectable } from '@angular/core';
import { GeometryTypeEnum, LayerEntity, LayerTypeEnum } from '@gishub/domain';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import WMTSLayer from '@arcgis/core/layers/WMTSLayer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import KMLLayer from '@arcgis/core/layers/KMLLayer';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Point from '@arcgis/core/geometry/Point';
import Geometry from '@arcgis/core/geometry/Geometry';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import Symbol from '@arcgis/core/symbols/Symbol';
import Color from '@arcgis/core/Color';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import type FeatureReductionCluster from '@arcgis/core/layers/support/FeatureReductionCluster';
import { LayerSymbolFactory, LayerStyleConfig } from './layer-symbol-factory';
import { Configuration } from '../../../shared/configuration/configuration';
import { LayerRepositoryProvider } from '../../../shared/providers/repository-providers';

@Injectable({ providedIn: 'root' })
export class ArcGisLayerFactory {
  constructor(
    private readonly symbolFactory: LayerSymbolFactory,
    private readonly config: Configuration,
    private readonly layerRepo: LayerRepositoryProvider,
  ) {}

  async createLayer(entity: LayerEntity): Promise<Layer | null> {
    const proxyUrl = this.config.proxyUrl;
    switch (entity.type) {
      case LayerTypeEnum.MapServer:
        return this.createMapServerLayer(entity, proxyUrl);
      case LayerTypeEnum.FeatureServer:
        return this.createFeatureLayer(entity, proxyUrl);
      case LayerTypeEnum.GeoJson:
        return this.createGeoJsonLayer(entity);
      case LayerTypeEnum.VectorTileServer:
        return this.createVectorTileLayer(entity);
      case LayerTypeEnum.WMS:
        return this.createWmsLayer(entity);
      case LayerTypeEnum.WMTS:
        return this.createWmtsLayer(entity);
      case LayerTypeEnum.WFS:
        return this.createWfsLayer(entity);
      case LayerTypeEnum.KML:
        return this.createKmlLayer(entity);
      case LayerTypeEnum.WebTile:
        return this.createWebTileLayer(entity);
      case LayerTypeEnum.SubServer:
        return this.createSubLayer(entity, proxyUrl);
      case LayerTypeEnum.Group:
        return this.createGroupLayer(entity);
      default:
        return null;
    }
  }

  private async createMapServerLayer(entity: LayerEntity, proxyUrl: string): Promise<MapImageLayer> {
    const url = entity.hasProxy ? `${proxyUrl}${entity.url}` : entity.url!;
    const match = url.match(/^(https?:\/\/.+?\/MapServer)(?:\/(\d+))?/i);
    const baseUrl = match?.[1] ?? url;

    let sublayers: { id: number; visible: boolean; title?: string; labelsVisible?: boolean; popupEnabled?: boolean }[] = match?.[2]
      ? [{ id: Number(match[2]), visible: entity.visible, title: entity.name, labelsVisible: entity.labelsVisible, popupEnabled: entity.popupEnabled }]
      : [];

    if (!match?.[2]) {
      const fetched = await this.fetchServiceSublayers(baseUrl);
      if (fetched.length) {
        sublayers = fetched;
      }
    }

    return new MapImageLayer({
      url: baseUrl,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      minScale: entity.minScale,
      maxScale: entity.maxScale,
      sublayers: sublayers.length ? sublayers : undefined,
    });
  }

  private async fetchServiceSublayers(baseUrl: string): Promise<{ id: number; visible: boolean; title?: string }[]> {
    try {
      const res = await this.layerRepo.instance.getLayerMetadata(baseUrl);
      const layers = res?.data?.layers ?? [];
      return layers
        .filter((sublayer: any) => sublayer && sublayer.id != null)
        .map((sublayer: any) => ({
          id: Number(sublayer.id),
          visible: sublayer.defaultVisibility !== false,
          title: sublayer.name,
        }))
        .filter((sublayer: { id: number }) => !Number.isNaN(sublayer.id));
    } catch (error) {
      console.warn(`[ArcGisLayerFactory] No se pudieron resolver las subcapas de '${baseUrl}':`, error);
      return [];
    }
  }

  /**
   * Modo de render de una capa cliente: decide qué se le pone **encima** al renderer del servicio.
   *
   * Se resuelve una sola vez por capa para que las reglas de exclusividad no queden repartidas en
   * varios `if` independientes (que es como se colaban combinaciones contradictorias antes).
   * Las exclusiones no son libremente elegibles, son límite del SDK:
   *
   * - El heatmap gana sobre el agrupamiento: `FeatureReductionCluster` no admite una capa con
   *   `HeatmapRenderer` ("Not supported for any layer with a HeatmapRenderer"), y la pregunta que
   *   responde cada uno es distinta ("¿dónde se concentra?" vs. "¿qué es este punto?").
   * - Heatmap y cluster son exclusivos de geometría de punto.
   */
  private resolveRenderMode(entity: LayerEntity): 'heatmap' | 'cluster' | 'flow' | 'default' {
    const isPoint = entity.geometryType === GeometryTypeEnum.Point;
    if (entity.heatmapPoints && isPoint) return 'heatmap';
    if (entity.clusterPoints && isPoint) return 'cluster';
    if (entity.animatedFlow) return 'flow';
    return 'default';
  }

  private createFeatureLayer(entity: LayerEntity, proxyUrl: string): FeatureLayer {
    const url = entity.hasProxy ? `${proxyUrl}${entity.url}` : entity.url!;
    const mode = this.resolveRenderMode(entity);
    const layer = new FeatureLayer({
      url,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      minScale: entity.minScale,
      maxScale: entity.maxScale,
      definitionExpression: entity.definitionQuery ?? undefined,
      outFields: entity.queryFields && entity.queryFields.length > 0 ? entity.queryFields : ['*'],
      // Una capa con `labelField` necesita las etiquetas encendidas para dibujar su
      // `labelingInfo`. El heatmap es la excepción: sobre una superficie de densidad no hay
      // símbolos que etiquetar.
      labelsVisible: mode === 'heatmap' ? false : entity.labelField ? true : entity.labelsVisible,
      popupTemplate: this.createPopupTemplate(entity),
    });
    if (mode === 'heatmap') {
      // Sustituye al renderer del servicio por la superficie de densidad (ponderable por campo).
      layer.renderer = this.symbolFactory.createHeatmapRenderer(
        entity.heatmapField ? { field: entity.heatmapField } : undefined,
      );
    } else if (mode === 'flow') {
      layer.renderer = this.symbolFactory.createAnimatedFlowRenderer();
    } else if (mode === 'cluster') {
      // Agrupamiento nativo de puntos (SDK): el cluster muestra el conteo —y los resúmenes
      // configurados— como etiqueta centrada sobre su propia burbuja, y el clic acerca la vista
      // al recuadro de los puntos. El agrupamiento se mantiene salvo que la capa defina
      // `clusterMaxScale`, que es cuando la burbuja se resuelve en puntos individuales.
      layer.featureReduction = this.symbolFactory.createClusterReduction({
        ...(entity.clusterMaxScale ? { maxScale: entity.clusterMaxScale } : {}),
        ...(entity.clusterAggregates?.length ? { aggregates: entity.clusterAggregates } : {}),
      });
      // El estilo de la burbuja se deriva del renderer real de la capa, que el servicio entrega
      // junto con la capa: se aplica en cuanto ésta carga, sin consultas extra al servicio.
      void this.applyClusterSymbol(layer);
    }
    // Etiqueta propia del cliente: solo si la capa no está agrupada (los clusters ya llevan
    // su etiqueta con el conteo) ni pintada como mapa de calor (que no dibuja símbolos).
    if (mode !== 'heatmap' && mode !== 'cluster' && entity.labelField) {
      layer.labelingInfo = [
        this.symbolFactory.createLabelClass({
          field: entity.labelField,
          ...(entity.labelMinScale ? { minScale: entity.labelMinScale } : {}),
          ...(entity.labelMaxScale ? { maxScale: entity.labelMaxScale } : {}),
        }),
      ];
    }
    return layer;
  }

  /**
   * Asigna a la burbuja del cluster un símbolo derivado del color del renderer de la capa.
   *
   * El color se lee en runtime del renderer que la capa ya trae del servicio, para que la burbuja
   * comparta identidad con la capa sin duplicar en el catálogo el color de cada servicio. Se muta
   * `featureReduction.symbol` en vez de reasignar el `featureReduction` entero: la instancia
   * guarda estado interno de accessor que un spread perdería.
   *
   * Degradación limpia: si el renderer no expone un color usable o la carga falla, la burbuja
   * conserva el estilo autogenerado por el SDK.
   */
  private async applyClusterSymbol(layer: FeatureLayer): Promise<void> {
    try {
      await layer.when();
      const symbol = this.symbolFactory.createClusterSymbol(
        undefined,
        this.symbolFactory.resolveRendererColor(layer.renderer),
      );
      const reduction = layer.featureReduction as FeatureReductionCluster | undefined;
      if (symbol && reduction) {
        reduction.symbol = symbol;
      }
    } catch (error) {
      console.warn(`[ArcGisLayerFactory] No se pudo derivar el símbolo del cluster de '${layer.title}':`, error);
    }
  }

  private createGeoJsonLayer(entity: LayerEntity): GeoJSONLayer {
    return new GeoJSONLayer({
      url: entity.url!,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      minScale: entity.minScale,
      maxScale: entity.maxScale,
      labelsVisible: entity.labelsVisible,
      popupTemplate: this.createPopupTemplate(entity),
    });
  }

  private createVectorTileLayer(entity: LayerEntity): VectorTileLayer {
    return new VectorTileLayer({
      url: entity.url!,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
    });
  }

  private createWmsLayer(entity: LayerEntity): WMSLayer {
    const url = entity.hasProxy ? `${this.config.proxyUrl}${entity.url}` : entity.url!;
    return new WMSLayer({
      url,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      minScale: entity.minScale,
      maxScale: entity.maxScale,
      customLayerParameters: entity.metadata?.customParams,
    });
  }

  private createWmtsLayer(entity: LayerEntity): WMTSLayer {
    return new WMTSLayer({
      url: entity.url!,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
    });
  }

  private createWfsLayer(entity: LayerEntity): WFSLayer {
    return new WFSLayer({
      url: entity.url!,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      minScale: entity.minScale,
      maxScale: entity.maxScale,
    });
  }

  private createKmlLayer(entity: LayerEntity): KMLLayer {
    return new KMLLayer({
      url: entity.url!,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
    });
  }

  private createWebTileLayer(entity: LayerEntity): WebTileLayer {
    return new WebTileLayer({
      urlTemplate: entity.url!,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity ?? 0.85,
    });
  }

  private createSubLayer(entity: LayerEntity, proxyUrl: string): MapImageLayer {
    const url = entity.hasProxy ? `${proxyUrl}${entity.url}` : entity.url!;
    return new MapImageLayer({
      url,
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      sublayers: entity.layers?.map(child => ({
        id: Number(child.referenceSource) || 0,
        visible: child.visible,
        title: child.name,
        definitionExpression: child.definitionQuery ?? undefined,
        minScale: child.minScale,
        maxScale: child.maxScale,
      })),
    });
  }

  private async createGroupLayer(entity: LayerEntity): Promise<GroupLayer> {
    const children: Layer[] = [];
    if (entity.layers) {
      for (const child of entity.layers) {
        const layer = await this.createLayer(child);
        if (layer) children.push(layer);
      }
    }
    return new GroupLayer({
      id: entity.id,
      title: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
      layers: children,
    });
  }

  setLayerStyle(layer: Layer, style: LayerStyleConfig): void {
    if ('renderer' in layer && layer.type !== 'map-image') {
      const fLayer = layer as FeatureLayer | GeoJSONLayer;
      fLayer.renderer = this.symbolFactory.createRenderer(style);
    }
  }

  setBoundaryRenderer(layer: Layer): void {
    if ('renderer' in layer) {
      const fLayer = layer as FeatureLayer | GeoJSONLayer;
      fLayer.renderer = this.symbolFactory.createBoundaryRenderer();
    }
  }

  createGraphicFromEntity(feature: any): Graphic {
    const geom = this.createGeometry(feature.geometry);
    const symbol = this.createDefaultSymbol(geom);
    return new Graphic({ geometry: geom, symbol: symbol as any, attributes: feature.attributes });
  }

  private createGeometry(geometry: any): Geometry | null {
    if (!geometry) return null;
    if (geometry.type === 'polygon' || geometry.rings) {
      return new Polygon({ rings: geometry.rings ?? geometry.coordinates, spatialReference: { wkid: 4326 } });
    }
    if (geometry.type === 'polyline' || geometry.paths) {
      return new Polyline({ paths: geometry.paths ?? geometry.coordinates, spatialReference: { wkid: 4326 } });
    }
    if (geometry.type === 'point' || (geometry.x !== undefined && geometry.y !== undefined)) {
      return new Point({ longitude: geometry.x ?? geometry.coordinates?.[0], latitude: geometry.y ?? geometry.coordinates?.[1] });
    }
    return null;
  }

  private createDefaultSymbol(geom: Geometry | null): Symbol {
    if (geom instanceof Polygon) {
      return new SimpleFillSymbol({
        color: new Color([0, 120, 200, 0.2]),
        outline: new SimpleLineSymbol({ color: new Color([0, 80, 160, 1]), width: 2 }),
      });
    }
    if (geom instanceof Polyline) {
      return new SimpleLineSymbol({ color: new Color([0, 120, 200, 1]), width: 3 });
    }
    return new SimpleMarkerSymbol({
      color: new Color([0, 120, 200, 1]),
      size: 10,
      outline: new SimpleLineSymbol({ color: new Color([255, 255, 255, 1]), width: 2 }),
    });
  }

  private createPopupTemplate(entity: LayerEntity): PopupTemplate | undefined {
    if (!entity.popupEnabled) return undefined;
    const fields = entity.queryFields?.length
      ? entity.queryFields.map(f => ({ field: f, label: f }))
      : undefined;
    return new PopupTemplate({
      title: entity.displayField ? `{${entity.displayField}}` : entity.name,
      fieldInfos: fields,
      content: [{ type: 'fields', fieldInfos: fields }],
    });
  }
}
